
import { handleAxiosException } from '@/api'
import { FindByIdMyArqanaHorseRegistrationDto, MyArqanaRegistrationRessourceDto, MyArqanaRegistrationRessourceType, myRegistrationApi } from '@/api/auth/myHorseRegistration'
import ErrorAlert from '@/components/alert/ErrorAlert.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import TitlePage from '@/components/TitlePage.vue'
import snackbarStore from '@/store/snackbarStore'
import { logWarning } from '@/utils/log'
import Vue from 'vue'
import RegistrationStepper from '../RegistrationStepper.vue'

export default Vue.extend({
  components: { RegistrationStepper, ErrorAlert, TitlePage, ConfirmDialog },
  props: {
    registrationId: {
      required: true,
      type: Number,
    },
  },
  data() {
    return {
      loading: false,
      errorLoading: '',
      downloading: false,
      registration: {} as FindByIdMyArqanaHorseRegistrationDto,
      photos: [] as MyArqanaRegistrationRessourceDto[],
      videos: [] as MyArqanaRegistrationRessourceDto[],
      sending: false,
      sendingVideo: false,
      errorSendingPhoto: '',
      errorSendingVideo: '',
      pedigreeFile: undefined as File | undefined,

      photoUrl: '',
      showPhoto: false,

      deleting: 0,

      counterDrag: 0,
      deleteConfirmDialog: {
        active: false,
        resource: undefined as MyArqanaRegistrationRessourceDto | undefined,
      },
    }
  },
  methods: {
    dragLeave() {
      this.counterDrag -= 1
    },
    dragOver(ev: DragEvent) {
      ev.preventDefault() // Nécessaire pour que le drop marche
    },
    dragEnter() {
      // eslint-disable-next-line no-plusplus
      this.counterDrag++
    },
    onDrop(e: DragEvent) {
      if (this.counterDrag <= 0) {
        return
      }

      this.counterDrag = 0
      e.preventDefault()

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (e.dataTransfer!.files.length > 1) {
        snackbarStore.setSnackbar({ text: this.$t('error.only_one_file') as string, color: 'error' })
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const files = Array.from(e.dataTransfer!.files)
      const accepts = ['image/*', 'video/*']
      if (!files.every(f => accepts.some(a => {
        if (a.startsWith('.')) {
          return f.name.endsWith(a)
        } else if (a.endsWith('/*')) {
          return f.type.startsWith(a.slice(0, a.length - 1))
        } else {
          return f.type === a
        }
      }))) {
        snackbarStore.setSnackbar({ text: this.$t('error.wrong_format') as string, color: 'error' })
      } else {
        this.sendResource(files[0], files[0].type.startsWith('video/') ? 'VIDEO' : 'PICTURE')
      }
    },
    async deleteResource(r: MyArqanaRegistrationRessourceDto) {
      this.deleteConfirmDialog = {
        active: true,
        resource: r,
      }
    },
    async confirmDelete() {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const p = this.deleteConfirmDialog.resource!
      this.deleting = p.id
      try {
        await myRegistrationApi.deleteResource(p.id)
        if (p.type === 'PICTURE') {
          this.photos = this.photos.filter(it => it.id !== p.id)
        } else {
          this.videos = []
        }
      } catch (e) {
        this.errorSendingPhoto = this.$t(handleAxiosException(e)) as string
      }
      this.deleting = 0
    },
    seePhoto(p: MyArqanaRegistrationRessourceDto) {
      this.photoUrl = p.downloadUrl
    },
    async sendPhotoFromForm() {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const files = (this.$refs.pictureInput as HTMLInputElement).files!
      if (files.length !== 1) {
        logWarning(`Wrong size of files : ${files.length}`)
      }
      this.sendResource(files[0], 'PICTURE')
      ;(this.$refs.pictureInput as HTMLInputElement).value = ''
    },
    async sendVideoFromForm() {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const files = (this.$refs.videoInput as HTMLInputElement).files!
      if (files.length !== 1) {
        logWarning(`Wrong size of files : ${files.length}`)
      }
      this.sendResource(files[0], 'VIDEO')
    },
    downloadVideo(p: MyArqanaRegistrationRessourceDto) {
      window.open(p.downloadUrl)
    },
    async sendResource(file: File, type: MyArqanaRegistrationRessourceType) {
      if (type === 'PICTURE' && this.photos.length >= 4 || type === 'VIDEO' && this.videos.length >= 1) {
        return
      }
      this.errorSendingPhoto = ''
      this.errorSendingVideo = ''
      if (type === 'PICTURE') {
        this.sending = true
      } else {
        this.sendingVideo = true
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const resource = await myRegistrationApi.sendResource(this.registrationId, file, type)
        if (type === 'PICTURE') {
          this.photos.push(resource)
        } else {
          this.videos.push(resource)
        }
        snackbarStore.setSnackbar({ text: this.$t('success_send') as string, color: 'success' })
        this.pedigreeFile = undefined
      } catch (e) {
        if (this.sending) {
          this.errorSendingPhoto = this.$t(handleAxiosException(e)) as string
        } else {
          this.errorSendingVideo = this.$t(handleAxiosException(e)) as string
        }
      }
      this.sending = false
      this.sendingVideo = false
    },
    async load() {
      this.loading = true
      Promise.all([
        myRegistrationApi.getAllResources(this.registrationId).then(resources => {
          this.photos = resources.filter(it => it.type === 'PICTURE')
          this.videos = resources.filter(it => it.type === 'VIDEO')
        }),
        myRegistrationApi.findById(this.registrationId).then((r) => {
          this.registration = r
        }),
      ]).catch(e => {
        this.errorLoading = this.$t(handleAxiosException(e)) as string
      }).finally(() => {
        this.loading = false
      })
    },
  },
  computed: {
    hasMaxPhotos(): boolean {
      return this.photos.length >= 4
    },
    hasMaxVideo(): boolean {
      return this.videos.length >= 1
    },
    showOverlayDrop(): boolean {
      return this.counterDrag > 0
    },
    sortedPhotos(): MyArqanaRegistrationRessourceDto[] {
      return this.photos
        .map(e => e)
        .sort((a, b) => {
          if (a.status === b.status) {
            return new Date(a.createdTime) < new Date(b.createdTime) ? -1 : 1
          }
          switch (a.status) {
            case 'ACCEPTED':
              return -1
            case 'WAITING':
              return b.status === 'REJECTED' ? -1 : 1
            case 'REJECTED':
              return 1
            default:
              return 0
          }
        })
    },
  },
  watch: {
    photoUrl(v) {
      if (v) {
        this.showPhoto = true
      }
    },
  },
  mounted() {
    this.load()

    document.addEventListener('dragleave', this.dragLeave)
    document.addEventListener('dragenter', this.dragEnter)
    document.addEventListener('dragover', this.dragOver)
    document.addEventListener('drop', this.onDrop)
  },
  destroyed() {
    document.removeEventListener('dragleave', this.dragLeave)
    document.removeEventListener('dragenter', this.dragEnter)
    document.removeEventListener('dragover', this.dragOver)
    document.removeEventListener('drop', this.onDrop)
  },
})
